import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Stack,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaTrashAlt } from 'react-icons/fa';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';

export const ProceduresModal = ({
  isOpen,
  onClose,
  type,
  procedureId,
  fetchAgain,
}) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;
  console.log('procedureId:', procedureId);

  const [selectedTab, setSelectedTab] = useState('options'); // For switching between procedure options and procedure steps

  // Validation Schema
  // const validationSchema = Yup.object().shape({
  //   procedureName: Yup.string().required('Procedure Name is required'),
  //   code: Yup.string().required('Code is required'),
  //   cost: Yup.number().required('Cost is required'),
  //   procedureOptions: Yup.array()
  //     .of(
  //       Yup.object().shape({
  //         option_name: Yup.string().required('Option Name is required'),
  //         additional_cost: Yup.number().required('Additional Cost is required'),
  //       }),
  //     )
  //     .test(
  //       'either-options-or-steps',
  //       'Either Procedure Options or Procedure Steps must be provided, but not both.',
  //       function (procedureOptions) {
  //         const { procedureSteps } = this.parent;
  //         return (
  //           (procedureOptions.length > 0 && procedureSteps.length === 0) ||
  //           (procedureSteps.length > 0 && procedureOptions.length === 0)
  //         );
  //       },
  //     ),
  //   procedureSteps: Yup.array()
  //     .of(
  //       Yup.object().shape({
  //         step_number: Yup.number().required('Step Number is required'),
  //         step_name: Yup.string().required('Step Name is required'),
  //         step_description: Yup.string().nullable(),
  //         step_price: Yup.number().required('Price is required'),
  //       }),
  //     )
  //     .test(
  //       'either-options-or-steps',
  //       'Either Procedure Options or Procedure Steps must be provided, but not both.',
  //       function (procedureSteps) {
  //         const { procedureOptions } = this.parent;
  //         return (
  //           (procedureSteps.length > 0 && procedureOptions.length === 0) ||
  //           (procedureOptions.length > 0 && procedureSteps.length === 0)
  //         );
  //       },
  //     ),
  // });

  // Validation Schema - removed the broken either-or test
  const validationSchema = Yup.object().shape({
    procedureName: Yup.string().required('Procedure Name is required'),
    code: Yup.string().required('Code is required'),
    cost: Yup.number().required('Cost is required'),
    procedureOptions: Yup.array().of(
      Yup.object().shape({
        option_name: Yup.string().required('Option Name is required'),
        additional_cost: Yup.number().required('Additional Cost is required'),
      }),
    ),
    procedureSteps: Yup.array().of(
      Yup.object().shape({
        step_number: Yup.number().required('Step Number is required'),
        step_name: Yup.string().required('Step Name is required'),
        step_description: Yup.string().nullable(),
        step_price: Yup.number().required('Price is required'),
      }),
    ),
  });

  const [loading, setLoading] = useState(false);
  const [procedureData, setProcedureData] = useState([]);
  console.log('🚀 ~ procedureData:', procedureData);

  // const [procedureOptions, setProcedureOptions] = useState(
  //   procedureData?.procedureOptions || [],
  // );
  // const [procedureSteps, setProcedureSteps] = useState(
  //   procedureData?.procedureSteps || [],
  // );

  const fetchProcedure = async () => {
    try {
      const response =
        await createAPIEndPoint('procedure/').fetchById(procedureId);
      setProcedureData(response.data.procedure || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching procedures');
    }
  };

  useEffect(() => {
    if (procedureId && type === 'edit') {
      fetchProcedure();
    }
  }, [procedureId, type]);

  // useEffect(() => {
  //   setProcedureOptions(procedureData?.options || []);
  //   setProcedureSteps(procedureData?.steps || []);
  // }, [procedureData]);

  const handleCreate = async (values, { resetForm }) => {
    console.log('Runnings');

    try {
      setLoading(true);

      // Construct payload according to required structure
      const payload = {
        user_id: userData?.id, // Assuming user_id comes from userData
        clinic_id,
        procedure_name: values.procedureName,
        cost: values.cost,
        code: values.code,
        options: values.procedureOptions.map((option) => ({
          name: option.option_name,
          additional_cost: option.additional_cost,
        })),
        procedure_steps: values.procedureSteps.map((step) => ({
          step_number: step.step_number,
          step_name: step.step_name,
          step_description: step.step_description,
          step_price: step.step_price,
        })),
      };

      if (procedureId) {
        // Update procedure
        await createAPIEndPoint('procedure/').update(procedureId, payload);
      } else {
        // Create new procedure
        await createAPIEndPoint('procedure/create').create(payload);
      }

      toast.success('Procedure saved successfully!');
      setLoading(false);
      resetForm();
      // setProcedureOptions([]);
      // setProcedureSteps([]);
      fetchAgain();
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Error creating procedure:', error);
      toast.error(error.response?.data?.error || 'An error occurred.');
    }
  };

  // Formik Setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      procedureName: procedureData?.procedure_name || '',
      code: procedureData?.code || '',
      cost: procedureData?.cost || '',
      procedureOptions: procedureData?.options || [],
      procedureSteps: procedureData?.steps || [],
    },
    validationSchema,
    onSubmit: handleCreate,
  });

  // ✅ Add Option — read/write only from Formik
  const addProcedureOption = () => {
    const updated = [
      ...formik.values.procedureOptions,
      { option_name: '', additional_cost: '' },
    ];
    formik.setFieldValue('procedureOptions', updated);
  };

  const removeProcedureOption = (index) => {
    const updated = formik.values.procedureOptions.filter(
      (_, i) => i !== index,
    );
    formik.setFieldValue('procedureOptions', updated);
  };

  const addProcedureStep = () => {
    const updated = [
      ...formik.values.procedureSteps,
      { step_number: '', step_name: '', step_description: '', step_price: '' },
    ];
    formik.setFieldValue('procedureSteps', updated);
  };

  const removeProcedureStep = (index) => {
    const updated = formik.values.procedureSteps.filter((_, i) => i !== index);
    formik.setFieldValue('procedureSteps', updated);
  };

  return (
    <Modal
      isOpen={isOpen}
      // onClose={() => {
      //   onClose();
      //   setProcedureData([]);
      // }}
      onClose={() => {
        formik.resetForm();
        setProcedureData([]); // keep this to clear fetched data
        onClose();
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        pb="16px"
        maxWidth={{ base: '90%', md: '500px' }}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Procedure' : 'Add New Procedure'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody maxHeight="70vh" overflowY="auto" pb={2}>
            {/* Procedure Name Field */}
            <FormControl
              isInvalid={
                formik.touched.procedureName && formik.errors.procedureName
              }
              mb={2}
            >
              <FormLabel mb={0}>Procedure Name</FormLabel>
              <Input
                name="procedureName"
                placeholder="Enter Procedure Name"
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                value={formik.values.procedureName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.procedureName && formik.errors.procedureName && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.procedureName}
                </Text>
              )}
            </FormControl>
            <FormControl
              isInvalid={formik.touched.code && formik.errors.code}
              mb={2}
            >
              <FormLabel mb={0}>Code</FormLabel>
              <Input
                name="code"
                placeholder="Enter Code"
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.code && formik.errors.code && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.code}
                </Text>
              )}
            </FormControl>
            <FormControl
              isInvalid={formik.touched.cost && formik.errors.cost}
              mb={2}
            >
              <FormLabel mb={0}>Cost</FormLabel>
              <Input
                name="cost"
                placeholder="Enter Cost"
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                value={formik.values.cost}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.cost && formik.errors.cost && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.cost}
                </Text>
              )}
            </FormControl>

            {/* Procedure Tabs */}
            <Stack direction="row" spacing={4} my={4}>
              <Button
                onClick={() => setSelectedTab('options')}
                colorScheme={selectedTab === 'options' ? 'brand' : 'teal'}
                w="50%"
              >
                Procedure Options
              </Button>
              <Button
                onClick={() => setSelectedTab('steps')}
                colorScheme={selectedTab === 'steps' ? 'brand' : 'teal'}
                w="50%"
              >
                Procedure Steps
              </Button>
            </Stack>

            {/* Procedure Options */}
            {selectedTab === 'options' && (
              <>
                {/* {procedureOptions.map((option, index) => ( */}
                {formik.values.procedureOptions.map((option, index) => (
                  <Stack
                    key={index}
                    spacing={3}
                    mb={3}
                    border="1px solid #E3E7EA"
                    p={4}
                    borderRadius="16px"
                  >
                    <FormControl
                      isInvalid={
                        formik.touched.procedureOptions?.[index]?.option_name &&
                        formik.errors.procedureOptions?.[index]?.option_name
                      }
                    >
                      <FormLabel>Option Name</FormLabel>
                      <Input
                        name={`procedureOptions[${index}].option_name`}
                        placeholder="Enter Option Name"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureOptions[index]?.option_name ||
                          ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>
                    <FormControl
                      isInvalid={
                        formik.touched.procedureOptions?.[index]
                          ?.additional_cost &&
                        formik.errors.procedureOptions?.[index]?.additional_cost
                      }
                    >
                      <FormLabel>Additional Cost</FormLabel>
                      <Input
                        name={`procedureOptions[${index}].additional_cost`}
                        type="number"
                        placeholder="Enter Additional Cost"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureOptions[index]
                            ?.additional_cost || ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>

                    <Button
                      colorScheme="red"
                      onClick={() => removeProcedureOption(index)}
                    >
                      <FaTrashAlt style={{ marginRight: 5 }} /> Remove
                    </Button>
                  </Stack>
                ))}
                <Button onClick={addProcedureOption} colorScheme="blue" mb={4}>
                  Add Option
                </Button>
              </>
            )}

            {/* Procedure Steps */}
            {selectedTab === 'steps' && (
              <>
                {/* {procedureSteps.map((step, index) => ( */}
                {formik.values.procedureSteps.map((step, index) => (
                  <Stack
                    key={index}
                    spacing={3}
                    mb={3}
                    border="1px solid #E3E7EA"
                    p={4}
                    borderRadius="16px"
                  >
                    <FormControl
                      isInvalid={
                        formik.touched.procedureSteps?.[index]?.step_number &&
                        formik.errors.procedureSteps?.[index]?.step_number
                      }
                    >
                      <FormLabel>Step Number</FormLabel>
                      <Input
                        name={`procedureSteps[${index}].step_number`}
                        type="number"
                        placeholder="Enter Step Number"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureSteps[index]?.step_number || ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>
                    <FormControl
                      isInvalid={
                        formik.touched.procedureSteps?.[index]?.step_name &&
                        formik.errors.procedureSteps?.[index]?.step_name
                      }
                    >
                      <FormLabel>Step Name</FormLabel>
                      <Input
                        name={`procedureSteps[${index}].step_name`}
                        placeholder="Enter Step Name"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureSteps[index]?.step_name || ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>
                    <FormControl
                      isInvalid={
                        formik.touched.procedureSteps?.[index]
                          ?.step_description &&
                        formik.errors.procedureSteps?.[index]?.step_description
                      }
                    >
                      <FormLabel>Step Description</FormLabel>
                      <Input
                        name={`procedureSteps[${index}].step_description`}
                        placeholder="Enter Step Description"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureSteps[index]
                            ?.step_description || ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>
                    <FormControl
                      isInvalid={
                        formik.touched.procedureSteps?.[index]?.step_price &&
                        formik.errors.procedureSteps?.[index]?.step_price
                      }
                    >
                      <FormLabel>Price</FormLabel>
                      <Input
                        name={`procedureSteps[${index}].step_price`}
                        type="number"
                        placeholder="Enter Price"
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={
                          formik.values.procedureSteps[index]?.step_price || ''
                        }
                        onChange={formik.handleChange}
                      />
                    </FormControl>
                    <Button
                      colorScheme="red"
                      onClick={() => removeProcedureStep(index)}
                    >
                      <FaTrashAlt style={{ marginRight: 5 }} /> Remove
                    </Button>
                  </Stack>
                ))}
                <Button onClick={addProcedureStep} colorScheme="blue" mb={4}>
                  Add Step
                </Button>
              </>
            )}
          </ModalBody>
          <ModalFooter pt={0}>
            <Button
              type="submit"
              colorScheme="brand"
              w="100%"
              mt={4}
              isLoading={formik.isSubmitting}
            >
              {type === 'edit' ? 'Edit Procedure' : 'Add New Procedure'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
